/**
 * 紙の撓み（CPU 側のベジェ頂点変形・FR-FLIP-01）。
 *
 * シートは背表紙を原点とし、自由端へ向かう中心線を 3 次ベジェで表す。
 * 曲がり量は sin(angle) に比例させるため、閉じた状態（angle=0）と着地状態（angle=π）では
 * 必ず平面に戻る。カスタムシェーダは使わない（設計書の方針）。
 */
import { CubicBezierCurve3, Vector3 } from 'three';
import { FLEX, SEGMENTS } from './constants';

/** シートが立っている側（+1 = 右・-1 = 左）。 */
export type SheetSide = 1 | -1;

/** 二次イージング（硬い表紙のめくりに使う）。 */
export function easeQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

/**
 * 背表紙（原点）から自由端へ向かう中心線。
 * @param width シートの幅（CSS px = world unit）
 * @param angle めくり角（0 = 閉じている・π = 反対側へ倒れきった）
 * @param flex 撓みやすさ（0 = 硬い板）
 * @param side +1 = 右のシート（+x へ伸びる）／-1 = 左のシート
 */
export function sheetCurve(
  width: number,
  angle: number,
  flex: number = FLEX,
  side: SheetSide = 1,
): CubicBezierCurve3 {
  const bend = flex * Math.sin(angle);
  // 撓むぶん、見かけの弦長はわずかに縮む（紙は伸びないため）。
  const chord = width * (1 - 0.12 * bend);
  // 背表紙側の接線は遅れ、自由端側の接線は先行する — これが紙のカールになる。
  const lag = 0.9 * bend;
  const lead = 0.6 * bend;

  const polar = (radius: number, theta: number): Vector3 =>
    new Vector3(side * radius * Math.cos(theta), 0, radius * Math.sin(theta));

  const p0 = new Vector3(0, 0, 0);
  const p3 = polar(chord, angle);
  const p1 = polar(chord / 3, angle - lag);
  const p2 = p3.clone().sub(polar(chord / 3, angle + lead));
  return new CubicBezierCurve3(p0, p1, p2, p3);
}

/**
 * 各頂点が中心線のどのサンプル点に対応するかを求める（構築時に 1 回だけ算出する）。
 * @param xs 頂点の x 座標（背表紙からの距離。符号は側による）
 */
export function columnIndices(
  xs: ArrayLike<number>,
  width: number,
  segments: number = SEGMENTS,
): Uint16Array {
  const columns = new Uint16Array(xs.length);
  for (let i = 0; i < xs.length; i += 1) {
    const ratio = width === 0 ? 0 : Math.abs(xs[i] ?? 0) / width;
    columns[i] = Math.min(segments, Math.max(0, Math.round(ratio * segments)));
  }
  return columns;
}

/**
 * めくり進捗（0〜1）から角度を求める。
 * 硬いシート（表紙・flex=0）は角度そのものをイージングし、柔らかいシートは角度を線形に進めて
 * 撓みの幾何変化に「ため」を作らせる（dflip の挙動に合わせる）。
 */
export function flipAngle(progress: number, rigid: boolean): number {
  const t = Math.min(1, Math.max(0, progress));
  return Math.PI * (rigid ? easeQuad(t) : t);
}
